/**
 * Demographic performance heatmap — shows performance intensity by
 * age group × gender (or other dimensions).
 */

import { ResponsiveHeatMap } from '@nivo/heatmap'
import { useNivoTheme } from './nivoTheme'

export interface HeatmapCell {
  id: string       // row label (e.g., "18-24")
  data: Array<{
    x: string      // column label (e.g., "Male")
    y: number      // metric value (e.g., ROAS)
  }>
}

interface DemographicHeatmapProps {
  data: HeatmapCell[]
  height?: number
  highlights?: string[]
  valueLabel?: string
  colorScheme?: 'performance' | 'spend'
}

export function DemographicHeatmap({
  data,
  height = 300,
  highlights: _highlights,
  valueLabel = 'ROAS',
  colorScheme = 'performance',
}: DemographicHeatmapProps) {
  const theme = useNivoTheme()

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        No demographic data available
      </div>
    )
  }

  const colors = colorScheme === 'performance'
    ? { type: 'diverging' as const, scheme: 'red_yellow_green' as const, divergeAt: 0.4 }
    : { type: 'sequential' as const, scheme: 'blues' as const }

  return (
    <div style={{ height }}>
      <ResponsiveHeatMap
        data={data}
        theme={theme}
        margin={{ top: 40, right: 20, bottom: 20, left: 80 }}
        valueFormat={(v: number) => `${v.toFixed(1)}x`}
        colors={colors}
        emptyColor="#f3f4f6"
        borderWidth={2}
        borderColor={{ from: 'color', modifiers: [['brighter', 0.5]] }}
        labelTextColor={{ from: 'color', modifiers: [['brighter', 3]] }}
        hoverTarget="cell"
        motionConfig="gentle"
        tooltip={({ cell }) => (
          <div className="bg-popover rounded-lg shadow-lg px-3 py-2 text-sm border border-border">
            <p className="font-semibold text-popover-foreground">
              {cell.serieId} × {String(cell.data.x)}
            </p>
            <p className="text-muted-foreground">
              {valueLabel}: <span className="font-medium">{Number(cell.value).toFixed(2)}x</span>
            </p>
          </div>
        )}
        legends={[{
          anchor: 'top' as const,
          translateX: 0,
          translateY: -30,
          length: 200,
          thickness: 8,
          direction: 'row' as const,
          tickPosition: 'after' as const,
          tickSize: 3,
          tickSpacing: 4,
          ticks: 5,
          title: `${valueLabel} →`,
          titleAlign: 'start' as const,
          titleOffset: 4,
        }]}
      />
    </div>
  )
}
